import { useState } from 'react';
import { DEFAULT_CARS, formatPence } from '@london-life/contracts';
import { api } from '../lib/api';
import { useCatalogue } from '../lib/content';
import { actionKey } from '../lib/format';
import { PhoneAppFrame, usePhoneApp, type PhoneAppProps } from '../lib/phone-app';

// Cars, as Lagos Life's 🚗 Cars: the garage (which car you drive, its condition, a service, selling) and the
// showroom's floor (bought there: the app sends you to the showroom when you are not in it)
export function CarsScreen(props: PhoneAppProps) {
  const content = useCatalogue()?.cars ?? DEFAULT_CARS;
  const { reply, failure, busy, load, run } = usePhoneApp(() => api.cars.mine(), props);
  const [selling, setSelling] = useState<string | null>(null);
  const modelOf = (id: string) => content.models.find((model) => model.id === id);
  const driven = reply?.cars.find((car) => car.driving);
  const drivenModel = driven ? modelOf(driven.model) : undefined;
  return (
    <PhoneAppFrame
      id="cars"
      title="🚗 Cars"
      className="cars-screen"
      closeLabel="Close Cars"
      onClose={props.onClose}
      failure={failure}
      onRetry={() => void load()}
      loading={reply ? undefined : 'Opening the garage…'}
    >
      {reply && (
        <div className="business-detail">
          <section className="staff-summary cars-summary" aria-label="Your garage">
            <span aria-hidden="true">{drivenModel?.emoji ?? '🚘'}</span>
            <span>
              <strong>
                {drivenModel
                  ? `${drivenModel.label}`
                  : reply.cars.length
                    ? `${reply.cars.length} in the garage`
                    : 'No car yet'}
              </strong>
              <small>
                {drivenModel
                  ? reply.driver && !drivenModel.bike
                    ? 'Your driver takes the wheel: no fuel to pay, and quicker'
                    : `${formatPence(drivenModel.fuelPence)} of fuel a trip · "${drivenModel.nick}" on the travel sheet`
                  : `${reply.showroomName} has everything from a scooter to a hypercar. Buy one there, and "Car" joins the travel sheet.`}
              </small>
            </span>
          </section>
          {reply.cars.length > 0 && (
            <ul className="staff-list cars-list" aria-label="Your cars">
              {reply.cars.map((car) => {
                const model = modelOf(car.model);
                if (!model) return null;
                return (
                  <li key={car.id} className={car.driving ? 'driving' : ''}>
                    <span aria-hidden="true">{model.emoji}</span>
                    <span>
                      <strong>
                        {model.label}
                        {car.driving ? ' · driving' : ''}
                      </strong>
                      <small>
                        Condition {car.condition}%{car.condition < content.tiredBelow ? ' · tired, needs a service' : ''}{' '}
                        · sells for {formatPence(car.resalePence)}
                      </small>
                      <span className="cars-actions">
                        {!car.driving && (
                          <button
                            type="button"
                            className="flights-terminal"
                            disabled={busy}
                            onClick={() => void run(() => api.cars.drive(car.id))}
                          >
                            Drive this
                          </button>
                        )}
                        {car.servicePence > 0 && (
                          <button
                            type="button"
                            className="flights-terminal"
                            disabled={busy || !reply.atShowroom || reply.walletPence < car.servicePence}
                            title={reply.atShowroom ? undefined : `A service is done at ${reply.showroomName}`}
                            onClick={() => void run(() => api.cars.service(car.id))}
                          >
                            Service · {formatPence(car.servicePence)}
                          </button>
                        )}
                        {selling !== car.id && (
                          <button
                            type="button"
                            className="flights-terminal"
                            disabled={busy || !reply.atShowroom}
                            title={reply.atShowroom ? undefined : `Sold at ${reply.showroomName}`}
                            onClick={() => setSelling(car.id)}
                          >
                            Sell…
                          </button>
                        )}
                      </span>
                      {selling === car.id && (
                        <span className="jet-sell">
                          <span>
                            Sell the {model.label} for {formatPence(car.resalePence)}?
                          </span>
                          <button type="button" onClick={() => setSelling(null)} disabled={busy}>
                            Keep
                          </button>
                          <button
                            type="button"
                            className="jet-sell-yes"
                            disabled={busy}
                            onClick={() => void run(() => api.cars.sell(car.id)).then(() => setSelling(null))}
                          >
                            Sell
                          </button>
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          <strong className="business-step-title">{reply.showroomName.toUpperCase()}</strong>
          {!reply.atShowroom && (
            <button
              type="button"
              className="city-card-go"
              onClick={() => props.onPlace?.(reply.showroomPlaceId)}
              disabled={!props.onPlace}
            >
              Go to {reply.showroomName} · buy, sell and service there
            </button>
          )}
          <ul className="staff-list cars-list" aria-label="On the showroom floor">
            {reply.showroom.map((offer) => {
              const model = modelOf(offer.model);
              if (!model) return null;
              const canTrade = offer.tradeInPence !== null && reply.atShowroom && !offer.blocked?.startsWith('In your');
              return (
                <li key={model.id} className={offer.blocked ? 'blocked' : ''}>
                  <span aria-hidden="true">{model.emoji}</span>
                  <span>
                    <strong>
                      {model.label} · "{model.nick}"
                    </strong>
                    <small>
                      {formatPence(model.pricePence)} · {formatPence(model.fuelPence)} of fuel a trip
                      {model.bike ? ' · a bike' : ''}
                    </small>
                    <p>{model.blurb}</p>
                    {offer.blocked && !canTrade ? (
                      <small className="staff-blocked">🔒 {offer.blocked}</small>
                    ) : (
                      <span className="cars-actions">
                        {!offer.blocked && (
                          <button
                            type="button"
                            className="city-card-go"
                            disabled={busy}
                            onClick={() => void run(() => api.cars.buy({ model: model.id }, actionKey('car')))}
                          >
                            Buy · {formatPence(offer.pricePence)}
                          </button>
                        )}
                        {canTrade && (
                          <button
                            type="button"
                            className="flights-terminal"
                            disabled={busy || reply.walletPence < offer.tradeInPence!}
                            onClick={() =>
                              void run(() => api.cars.buy({ model: model.id, tradeIn: true }, actionKey('car')))
                            }
                          >
                            Trade the {drivenModel?.label} in · {formatPence(offer.tradeInPence!)}
                          </button>
                        )}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="business-muted">
            Cars sell back for {Math.round(content.resaleShare * 100)}% of the price. A car wears with the weeks and
            the miles; under {content.tiredBelow}% it is slow to start, and a service at the showroom puts it right.
            Up to {reply.garageMax} cars in the garage. A driver on the Staff payroll drives anything but a bike.
          </p>
        </div>
      )}
    </PhoneAppFrame>
  );
}
